
import { useState, useEffect, useCallback } from 'react';
import { generateCsrfToken, validateCsrfToken } from '@/utils/securityUtils';

export const useCsrf = () => {
  const [csrfToken, setCsrfToken] = useState<string>('');
  
  // Initialize token when component mounts
  useEffect(() => {
    const newToken = generateCsrfToken();
    setCsrfToken(newToken);
    sessionStorage.setItem('csrfToken', newToken);
    
    // Set up automatic token rotation (every 15 minutes)
    const tokenRotationInterval = setInterval(() => {
      refreshCsrfToken();
    }, 15 * 60 * 1000);
    
    return () => {
      clearInterval(tokenRotationInterval);
    };
  }, []);
  
  // Refresh token on demand
  const refreshCsrfToken = useCallback(() => {
    const newToken = generateCsrfToken();
    setCsrfToken(newToken);
    sessionStorage.setItem('csrfToken', newToken);
    return newToken;
  }, []);
  
  // Get stored token
  const getStoredCsrfToken = useCallback((): string => {
    return sessionStorage.getItem('csrfToken') || '';
  }, []);
  
  // Validate token and refresh if needed
  const validateAndRefreshToken = useCallback((token: string): boolean => {
    const storedToken = getStoredCsrfToken();
    const isValid = validateCsrfToken(token, storedToken);
    
    if (!isValid) {
      // Token is invalid or expired, refresh it
      refreshCsrfToken();
      return false;
    }
    
    return true;
  }, [getStoredCsrfToken, refreshCsrfToken]);
  
  // Add token to forms and requests
  const addTokenToFormData = useCallback((formData: FormData): FormData => {
    formData.append('csrf_token', csrfToken);
    return formData;
  }, [csrfToken]);
  
  const addTokenToRequestHeaders = useCallback((headers: HeadersInit = {}): HeadersInit => {
    return {
      ...headers,
      'X-CSRF-Token': csrfToken,
    };
  }, [csrfToken]);
  
  return {
    csrfToken,
    refreshCsrfToken,
    getStoredCsrfToken,
    validateAndRefreshToken,
    addTokenToFormData,
    addTokenToRequestHeaders
  };
};
